import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faPen,
} from "@fortawesome/free-solid-svg-icons";
import HelmetInfo from "Components/HelmetInfo/HelmetInfo";
import { toast } from "react-toastify";
import {
  collection,
  doc,
  getDocs,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";

const AllDoctors = () => {
  const { t } = useTranslation();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const router = useNavigate();

  const fetchCards = async () => {
    setLoading(true);
    try {
      const collectionRef = collection(db, "OurDoctors", "Doctors", "Cards");
      const snapshot = await getDocs(collectionRef);
      let cardsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Fix order if missing
      cardsData = cardsData.map((card, index) => ({
        ...card,
        order: typeof card.order === "number" ? card.order : index,
      }));

      // Sort ascending
      cardsData.sort((a, b) => a.order - b.order);

      setCards(cardsData);
    } catch (error) {
      console.error("Error fetching cards:", error);
      toast.error(t("FailedToLoadDoctors"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCards();
  }, []);

  // Update order immediately on input change and update Firestore
  const handleOrderChange = async (id, newOrderStr) => {
    const newOrder = parseInt(newOrderStr);
    if (isNaN(newOrder) || newOrder < 0) return;

    // Update local state optimistically
    const updatedCards = cards.map((card) =>
      card.id === id ? { ...card, order: newOrder } : card
    );

    // Sort locally
    updatedCards.sort((a, b) => a.order - b.order);
    setCards(updatedCards);

    try {
      // Update in Firestore
      const docRef = doc(db, "OurDoctors", "Doctors", "Cards", id);
      await updateDoc(docRef, { order: newOrder });

      toast.success(t("OrderUpdatedSuccessfully"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 1000,
      });
    } catch (error) {
      console.error("Error updating order:", error);
      toast.error(t("FailedToUpdateOrder"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  const handleDeleteCard = async (cardId) => {
    try {
      const docRef = doc(db, "OurDoctors", "Doctors", "Cards", cardId);
      await deleteDoc(docRef);

      const updatedCards = cards.filter((card) => card.id !== cardId);
      setCards(updatedCards);

      toast.success(t("CardDeletedSuccessfully"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error deleting card:", error);
      toast.error(t("CardDeletionFailed"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  return (
    <>
      <HelmetInfo titlePage={t("allDoctors")} />
      <div className="project-management-request all-main">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>{t("Doctors")}</h2>
          <Link
            className="text-decoration-none d-flex align-items-center gap-2 btn-main button-home button-new-project"
            to={"/AddDoctor"}
          >
            <FontAwesomeIcon icon={faPlus} />
            {t("add New Doctor")}
          </Link>
        </div>

        <div className="form-project-management">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="row Servcis-Cards">
              {cards.length === 0 && (
                <div className="col-12 text-center py-5">
                  <p>{t("NoDoctorsFound")}</p>
                  <Link to="/AddDoctor" className="btn btn-primary">
                    <FontAwesomeIcon icon={faPlus} /> {t("AddYourFirstDoctor")}
                  </Link>
                </div>
              )}
              {cards.map((card) => (
                <div className="col-md-3 col-sm-6 mb-4" key={card.id}>
                  <div className="service-card p-3">
                    {card.firstImageUrl && (
                      <img
                        src={card.firstImageUrl}
                        alt={card.Name}
                        className="img-fluid rounded mb-2"
                        style={{ height: "150px", objectFit: "cover" }}
                      />
                    )}
                    <h5>{card.Name}</h5>
                    <p>{card.Title}</p>
                  <div className="mb-2 d-flex align-items-center gap-2" title={t("ChangeOrder")}>
  <label htmlFor={`order-${card.id}`} className="mb-0">
    <small>Order:</small>
  </label>

  <button
    className="btn btn-outline-secondary btn-sm"
    onClick={() => handleOrderChange(card.id, card.order - 1)}
    disabled={card.order <= 0}
    aria-label={t("DecreaseOrder")}
    style={{ padding: "2px 8px" }}
  >
    –
  </button>

  <input
    type="number"
    id={`order-${card.id}`}
    value={card.order}
    min={0}
    onChange={(e) => handleOrderChange(card.id, e.target.value)}
    style={{
      width: "50px",
      textAlign: "center",
      borderRadius: "4px",
      border: "1px solid #ced4da",
      height: "30px",
    }}
    aria-label={t("OrderInput")}
  />

  <button
    className="btn btn-outline-secondary btn-sm"
    onClick={() => handleOrderChange(card.id, card.order + 1)}
    aria-label={t("IncreaseOrder")}
    style={{ padding: "2px 8px" }}
  >
    +
  </button>
</div>

                    <div className="d-flex gap-2">
                      <Link
                        className="btn btn-sm btn-warning"
                        to={`/AddDoctor?edit=${card.id}`}
                        title={t("Edit")}
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </Link>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteCard(card.id)}
                        title={t("Delete")}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AllDoctors;
